/**
 * Product Registration and Warranty API Module
 * 
 * This module provides functions to interact with the product registration
 * and warranty verification APIs.
 * 
 * CORS Requirements:
 * The API server must be configured to allow cross-origin requests with the following headers:
 * - Access-Control-Allow-Origin: * (or specific domain)
 * - Access-Control-Allow-Methods: GET, OPTIONS
 * - Access-Control-Allow-Headers: Content-Type
 * 
 * The server must also respond to OPTIONS preflight requests with these headers.
 */

(function() {
  'use strict';

  // API Base URLs
  const API_BASE_URL = 'https://charbroil-customer-089cfb9b05bd.herokuapp.com/';
  const REGISTRATION_ENDPOINT = API_BASE_URL + 'registrations';
  const WARRANTY_ENDPOINT = API_BASE_URL + 'warranty';

  /**
   * Get registered products for a customer
   * 
   * @param {string} customerEmail - Customer email address (required)
   * @param {string} [customerLastName] - Customer last name (optional)
   * @returns {Promise} Promise that resolves with registered products data or rejects with error
   * 
   * @example
   * getRegisteredProducts('customer@example.com', 'Smith')
   *   .then(data => {
   *     console.log('Registered products:', data.itemRegistrations);
   *   })
   *   .catch(error => {
   *     console.error('Error:', error);
   *   });
   */
  window.getRegisteredProducts = async function(customerEmail, customerLastName) {
    // Validate required parameter
    if (!customerEmail || typeof customerEmail !== 'string' || customerEmail.trim() === '') {
      throw {
        success: false,
        error: 'CustomerEmail is required and must be a non-empty string',
        data: null
      };
    }

    // Build query parameters
    var params = new URLSearchParams();
    params.append('email', customerEmail.trim());
    
    if (customerLastName && typeof customerLastName === 'string' && customerLastName.trim() !== '') {
      params.append('lastname', customerLastName.trim());
    }

    // Build full URL
    var url = REGISTRATION_ENDPOINT + '?' + params.toString();

    try {
      // Make API call using fetch
      var response = await fetch(url, {
        method: 'GET',
        mode: 'cors'
      });

      // Check if response is ok
      if (!response.ok) {
        var errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = await response.text();
        }

        var errorMessage = 'API request failed with status: ' + response.status;
        if (errorData && errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          errorMessage = errorData.errors.map(function(err) {
            return err.message || err.name || 'Unknown error';
          }).join(', ');
        } else if (errorData && typeof errorData === 'string') {
          errorMessage = errorData;
        }

        throw {
          success: false,
          error: errorMessage,
          data: errorData,
          response: response,
          isCorsError: false
        };
      }

      // Parse JSON response
      var data = await response.json();

      // Check if response has expected structure
      if (data && typeof data === 'object') {
        
        return {
          success: true,
          error: null,
          data: data.body,
          response: response
        };
      } else {
        throw {
          success: false,
          error: 'Invalid response format from API',
          data: data,
          response: response
        };
      }
    } catch (error) {
      // Handle network errors and CORS issues
      if (error.name === 'TypeError' || error.message && error.message.includes('fetch')) {
        throw {
          success: false,
          error: 'Network error: Failed to connect to API. Please check your internet connection and ensure CORS is properly configured on the server.',
          data: null,
          response: null,
          isCorsError: true
        };
      }
      // Re-throw if it's already our error format
      if (error.success !== undefined) {
        throw error;
      }
      // Otherwise wrap the error
      throw {
        success: false,
        error: error.message || 'Failed to fetch registered products',
        data: null,
        response: null,
        isCorsError: false
      };
    }
  };

  /**
   * Check warranty status for a product registration
   * 
   * @param {number|string} registrationId - The registration ID to check warranty for
   * @param {number} [filterMonth=12] - Number of months to filter warranty (default: 12)
   * @returns {Promise} Promise that resolves with warranty data or rejects with error
   * 
   * @example
   * checkWarranty(255184, 12)
   *   .then(data => {
   *     console.log('Warranty status:', data.data.status);
   *   })
   *   .catch(error => {
   *     console.error('Error:', error);
   *   });
   */
  window.checkWarranty = async function(registrationId, filterMonth) {
    // Validate required parameter
    if (!registrationId) {
      throw {
        success: false,
        error: 'Registration ID is required',
        data: null
      };
    }

    // Set default filterMonth to 12 if not provided
    if (filterMonth === undefined || filterMonth === null) {
      filterMonth = 12;
    }

    // Convert registrationId to string for URL
    var regId = String(registrationId);
    
    // Build URL with query parameters
    var url = WARRANTY_ENDPOINT + '?registration=' + encodeURIComponent(regId) + '&FilterMonth=' + encodeURIComponent(filterMonth);

    try {
      // Make API call using fetch
      var response = await fetch(url, {
        method: 'GET',
        mode: 'cors'
      });

      // Check if response is ok
      if (!response.ok) {
        var errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = await response.text();
        }

        var errorMessage = 'API request failed with status: ' + response.status;
        if (errorData && errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          errorMessage = errorData.errors.map(function(err) {
            return err.message || err.name || 'Unknown error';
          }).join(', ');
        } else if (errorData && typeof errorData === 'string') {
          errorMessage = errorData;
        }

        throw {
          success: false,
          error: errorMessage,
          data: errorData,
          response: response,
          isCorsError: false
        };
      }

      // Parse JSON response
      var data = await response.json();

      // Check if response has expected structure
      if (data && typeof data === 'object') {
        // The API may return status: "Failed" with errors array
        // We still resolve this as success since it's a valid API response
        return {
          success: true,
          error: null,
          data: data,
          response: response
        };
      } else {
        throw {
          success: false,
          error: 'Invalid response format from API',
          data: data,
          response: response
        };
      }
    } catch (error) {
      // Handle network errors and CORS issues
      if (error.name === 'TypeError' || error.message && error.message.includes('fetch')) {
        throw {
          success: false,
          error: 'Network error: Failed to connect to API. Please check your internet connection and ensure CORS is properly configured on the server.',
          data: null,
          response: null,
          isCorsError: true
        };
      }
      // Re-throw if it's already our error format
      if (error.success !== undefined) {
        throw error;
      }
      // Otherwise wrap the error
      throw {
        success: false,
        error: error.message || 'Failed to check warranty status',
        data: null,
        response: null,
        isCorsError: false
      };
    }
  };

  // Export functions for use in other modules (if needed)
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      getRegisteredProducts: window.getRegisteredProducts,
      checkWarranty: window.checkWarranty
    };
  }

})();

